import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Download as DownloadIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { userService } from '../../services/userService';
import { availabilityService } from '../../services/availabilityService';
import { groupService } from '../../services/groupService';
import { roleService } from '../../services/roleService';
import { householdService } from '../../services/householdService';
import { groupTypeService } from '../../services/groupTypeService';
import { trackError } from '../../utils/sentryUtils';
import { useSentryTracking } from '../SentryComponentWrapper';
import * as Sentry from '@sentry/react';

// Simple TabPanel component for tabbed content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rosters-tabpanel-${index}`}
      aria-labelledby={`rosters-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const Rosters = () => {
  // Initialize Sentry tracking for this component
  useSentryTracking('Rosters', { includeUpdates: true, includeRender: true });

  const [tabValue, setTabValue] = useState(0);
  const [students, setStudents] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupTypes, setGroupTypes] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Search states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHousehold, setFilterHousehold] = useState('');

  // Load all students (users with student role) and related data
  const loadStudents = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get all users, roles, households, groups, and group types
      const [fetchedUsers, allRoles, allHouseholds, allGroups, allGroupTypes] = await Promise.all([
        userService.getAllUsers(),
        roleService.getAllRoles(),
        householdService.getAllHouseholds(),
        groupService.getAllGroups(),
        groupTypeService.getAllGroupTypes()
      ]);
      
      setAllUsers(fetchedUsers);
      setHouseholds(allHouseholds);
      setGroups(allGroups);
      setGroupTypes(allGroupTypes);
      
      // Filter for students
      const studentUsers = fetchedUsers.filter(user => 
        user.roleIds && user.roleIds.some(roleId => 
          allRoles.find(role => role.id === roleId && role.name === 'Student')
        )
      );
      
      setStudents(studentUsers);
    } catch (err) {
      console.error('Error loading students:', err);
      trackError(err, 'Failed to load students for roster');
      setError('Failed to load students. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, []);

  // Format availability data for CSV
  const formatAvailabilityForCSV = async (userId) => {
    try {
      const availability = await availabilityService.getUserAvailabilityByUserId(userId);
      const availableSlots = availability.filter(slot => slot.isAvailable);
      
      return availableSlots.map(slot => {
        const locationName = slot.locationName || 'Unknown Location';
        const dayOfWeek = slot.dayOfWeek || 'Unknown Day';
        const startTime = formatTimeForCSV(slot.startTime);
        const endTime = formatTimeForCSV(slot.endTime);
        return `${locationName} - ${dayOfWeek} ${startTime} to ${endTime}`;
      }).join('; ');
    } catch (err) {
      console.error('Error formatting availability for user:', userId, err);
      return 'Error loading availability';
    }
  };

  // Format time for CSV (convert from 24-hour to 12-hour format)
  const formatTimeForCSV = (timeString) => {
    if (!timeString) return '';
    
    // Handle different time formats
    const time = timeString.toString();
    const [hours, minutes] = time.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return time;
    }
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const displayMinutes = minutes.toString().padStart(2, '0');
    
    return `${displayHours}:${displayMinutes} ${period}`;
  };

  // Get league information for a student
  const getLeagueInfo = (user) => {
    if (!groups || groups.length === 0) return '';
    
    // Find groups where this user is a member and the group type includes 'League'
    const userLeagues = groups.filter(group => {
      // Check if user is in this group
      const isMember = group.userIds && group.userIds.includes(user.id);
      
      // Check if this is a league group - try multiple approaches
      let isLeague = false;
      
      // Method 1: Check groupTypeName directly
      if (group.groupTypeName && group.groupTypeName.toLowerCase().includes('league')) {
        isLeague = true;
      }
      
      // Method 2: Check groupTypeId against groupTypes
      if (!isLeague && group.groupTypeId && groupTypes.length > 0) {
        const groupType = groupTypes.find(gt => gt.id === group.groupTypeId);
        if (groupType && groupType.name && groupType.name.toLowerCase().includes('league')) {
          isLeague = true;
        }
      }
      
      // Method 3: Check group name pattern (fallback)
      if (!isLeague && group.name && group.name.toLowerCase().includes('league')) {
        isLeague = true;
      }
      

      
      return isMember && isLeague;
    });
    
    return userLeagues.map(league => league.name || league.groupName).join(', ');
  };

  // Get household codes for a user
  const getHouseholdCodes = (user) => {
    if (!user.householdIds || user.householdIds.length === 0) return '';
    
    const userHouseholds = households.filter(household => 
      user.householdIds.includes(household.id)
    );
    
    return userHouseholds.map(household => household.householdCode).join(', ');
  };

  // Get primary caregiver email for a household
  const getPrimaryCaregiverEmail = (household) => {
    if (!household || !household.primaryCaregiverId) return '';
    const caregiver = allUsers.find(u => u.id === household.primaryCaregiverId);
    return caregiver?.email || '';
  };

  // Get primary caregiver first and last name for a household
  const getPrimaryCaregiverFirstName = (household) => {
    if (!household || !household.primaryCaregiverId) return '';
    const caregiver = allUsers.find(u => u.id === household.primaryCaregiverId);
    return caregiver?.firstName || '';
  };

  const getPrimaryCaregiverLastName = (household) => {
    if (!household || !household.primaryCaregiverId) return '';
    const caregiver = allUsers.find(u => u.id === household.primaryCaregiverId);
    return caregiver?.lastName || '';
  };

  // Filter students based on search term and household
  const filteredStudents = students.filter(student => {
    const matchesSearch = !searchTerm || 
      student.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.preferredName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.school?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesHousehold = !filterHousehold || 
      getHouseholdCodes(student).toLowerCase().includes(filterHousehold.toLowerCase());
    
    return matchesSearch && matchesHousehold;
  });

  // Export students to CSV
  const exportToCSV = async () => {
    try {
      setExporting(true);
      setError('');
      setSuccess('');

      // Create a span to track the export operation
      await Sentry.startSpan(
        {
          op: "ui.click",
          name: "Export Students to CSV",
        },
        async (span) => {
          span.setAttribute("student_count", students.length);

          // Prepare CSV data
          const csvData = [];
          
          // Add header row
          csvData.push([
            'id',
            'district',
            'school',
            'first_name',
            'preferred_name',
            'middle_name',
            'last_name',
            'gender',
            'graduation_year',
            'tshirt_size',
            'household_code',
            'league',
            'user_created_timestamp',
            'availabilities'
          ]);

          // Process each student
          for (const student of students) {
            const availability = await formatAvailabilityForCSV(student.id);
            const league = getLeagueInfo(student);
            const householdCodes = getHouseholdCodes(student);
            
            csvData.push([
              student.id || '',
              student.district || '',
              student.school || '',
              student.firstName || '',
              student.preferredName || '',
              student.middleName || '',
              student.lastName || '',
              student.gender || '',
              student.graduationYear || '',
              student.tshirtSize || '',
              householdCodes,
              league,
              student.createdAt || '',
              availability
            ]);
          }

          // Convert to CSV string
          const csvContent = csvData.map(row => 
            row.map(field => {
              // Escape fields that contain commas, quotes, or newlines
              if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
                return `"${field.replace(/"/g, '""')}"`;
              }
              return field;
            }).join(',')
          ).join('\n');

          // Create and download file
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `students_roster_${new Date().toISOString().split('T')[0]}.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setSuccess(`Successfully exported ${students.length} students to CSV`);
        }
      );
    } catch (err) {
      console.error('Error exporting CSV:', err);
      trackError(err, 'Failed to export students to CSV');
      setError('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Export households to CSV
  const exportHouseholdsToCSV = async () => {
    try {
      setExporting(true);
      setError('');
      setSuccess('');

      await Sentry.startSpan(
        {
          op: 'ui.click',
          name: 'Export Households to CSV',
        },
        async (span) => {
          span.setAttribute('household_count', households.length);

          const csvData = [];
          // Header
          csvData.push([
            'household_name',
            'primary_caregiver_first_name',
            'primary_caregiver_last_name',
            'primary_caregiver_email',
            'household_code'
          ]);

          for (const household of households) {
            csvData.push([
              household.name || '',
              getPrimaryCaregiverFirstName(household),
              getPrimaryCaregiverLastName(household),
              getPrimaryCaregiverEmail(household),
              household.householdCode || ''
            ]);
          }

          const csvContent = csvData.map(row => 
            row.map(field => {
              if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
                return `"${field.replace(/"/g, '""')}"`;
              }
              return field;
            }).join(',')
          ).join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `households_export_${new Date().toISOString().split('T')[0]}.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setSuccess(`Successfully exported ${households.length} households to CSV`);
        }
      );
    } catch (err) {
      console.error('Error exporting households CSV:', err);
      trackError(err, 'Failed to export households to CSV');
      setError('Failed to export households CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const getDisplayName = (user) => {
    if (!user) return 'Unknown User';
    const name = user.preferredName || user.firstName || 'Unknown';
    const lastName = user.lastName || '';
    return `${name} ${lastName}`.trim();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Student Roster" />
          <Tab label="Household Export" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom color="primary">
              Student Roster
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and export all student information
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
            disabled={exporting || students.length === 0}
            sx={{ minWidth: 200 }}
          >
            {exporting ? 'Exporting...' : 'Export to CSV'}
          </Button>
        </Box>

        {/* Search and Filter Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search Students"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, or school..."
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Household Code</InputLabel>
                  <Select
                    value={filterHousehold}
                    onChange={(e) => setFilterHousehold(e.target.value)}
                    label="Filter by Household Code"
                  >
                    <MenuItem value="">All Households</MenuItem>
                    {households.map((household) => (
                      <MenuItem key={household.id} value={household.householdCode}>
                        {household.householdCode} - {household.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Paper elevation={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>School Info</TableCell>
                  <TableCell>Graduation Year</TableCell>
                  <TableCell>T-Shirt Size</TableCell>
                  <TableCell>Household Code</TableCell>
                  <TableCell>League</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" color="primary" />
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {getDisplayName(student)}
                          </Typography>
                          {student.preferredName && student.preferredName !== student.firstName && (
                            <Typography variant="caption" color="text.secondary">
                              Prefers: {student.preferredName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <SchoolIcon fontSize="small" color="primary" />
                        <Box>
                          <Typography variant="body2">
                            {student.school || 'Not specified'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {student.district || 'No district'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={student.graduationYear || 'Not specified'} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {student.tshirtSize || 'Not specified'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {getHouseholdCodes(student) || 'Not assigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getLeagueInfo(student) || 'No league'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {student.createdAt ? 
                          new Date(student.createdAt).toLocaleDateString() : 
                          'Unknown'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {filteredStudents.length === 0 && !loading && (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              No students found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || filterHousehold ? 'Try adjusting your search criteria.' : 'There are currently no students in the system.'}
            </Typography>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom color="primary">
              Household Export
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Export households with primary caregiver email and code
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportHouseholdsToCSV}
            disabled={exporting || households.length === 0}
            sx={{ minWidth: 200 }}
          >
            {exporting ? 'Exporting...' : 'Export to CSV'}
          </Button>
        </Box>

        <Paper elevation={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Household Name</TableCell>
                  <TableCell>Primary Caregiver First Name</TableCell>
                  <TableCell>Primary Caregiver Last Name</TableCell>
                  <TableCell>Primary Caregiver Email</TableCell>
                  <TableCell>Household Code</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {households.map((household) => (
                  <TableRow key={household.id} hover>
                    <TableCell>
                      <Typography variant="body2">{household.name || 'Untitled Household'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getPrimaryCaregiverFirstName(household) || ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getPrimaryCaregiverLastName(household) || ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getPrimaryCaregiverEmail(household) || 'Not assigned'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{household.householdCode || ''}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {households.length === 0 && !loading && (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              No households found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are currently no households in the system.
            </Typography>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
};

export default Rosters; 